import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase, callEdgeFunction } from './supabaseClient';
import AdminPageHero from './AdminPageHero';
import './SimpraAdminPage.css';

const SIMPRA_APPS = [
  { key: 'app', label: 'MMJ APP', suffix: 'APP' },
  { key: 'connect', label: 'MMJ APP Connect', suffix: 'CONNECT' },
  { key: 'access', label: 'Simpra Access Token', suffix: 'ACCESS' },
];

const KNOWN_SHOPS = [
  {
    key: 'bahcesehir',
    label: 'Bahçeşehir',
    propertyId: 'eb404fd6-e8ab-4773-8a85-19201501b5cb',
    propertySecret: 'SIMPRA_PROPERTY_ID_BAHCESEHIR',
  },
  {
    key: 'arenapark',
    label: 'Arenapark',
    propertyId: '83487dea-9b7c-4071-8abd-1443eed16cf7',
    propertySecret: 'SIMPRA_PROPERTY_ID_ARENAPARK',
  },
  {
    key: 'merkez',
    label: 'Merkez',
    propertyId: '70bdb2bd-c2c0-4b53-9e44-8844b7ea35a7',
    propertySecret: 'SIMPRA_PROPERTY_ID_MERKEZ',
  },
  {
    key: 'yesilkoy',
    label: 'Yeşilköy',
    propertyId: 'c1d2375e-1f8d-43f8-86b2-f9acb589a44f',
    propertySecret: 'SIMPRA_PROPERTY_ID_YESILKOY',
  },
  {
    key: 'taksim',
    label: 'Taksim',
    propertyId: 'b04bc6d1-9860-4a3b-824b-5aa67091e795',
    propertySecret: 'SIMPRA_PROPERTY_ID_TAKSIM',
  },
  {
    key: 'nisantasi',
    label: 'Nişantaşı',
    propertyId: 'ce6d5ee2-7e55-42ea-b003-d4ca543f2b6a',
    propertySecret: 'SIMPRA_PROPERTY_ID_NISANTASI',
  },
];

/** Flat list: shop × app (secret picker / setup). */
const KNOWN_PROPERTIES = KNOWN_SHOPS.flatMap((shop) =>
  SIMPRA_APPS.map((app) => ({
    key: `${shop.key}_${app.key}`,
    shopKey: shop.key,
    label: `${shop.label} · ${app.label}`,
    shopLabel: shop.label,
    appLabel: app.label,
    propertyId: shop.propertyId,
    secretKey: `SIMPRA_TOKEN_${shop.key.toUpperCase()}_${app.suffix}`,
    propertySecret: shop.propertySecret,
  })),
);

const DEFAULT_SECRET_KEY = 'SIMPRA_TOKEN_BAHCESEHIR_APP';

function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length <= 13 ? s : `${s.slice(0, 8)}…${s.slice(-4)}`;
}

function discountLabel(d) {
  const amt = Number(d.value);
  const amountText = d.type === 'amount' ? `${amt} TL` : `%${amt}`;
  const translations = Array.isArray(d.translations) ? d.translations : [];
  const tr = translations.find((t) => t?.locale === 'tr' && t?.name);
  const en = translations.find((t) => t?.locale === 'en' && t?.name);
  const name = tr?.name || en?.name || translations.find((t) => t?.name)?.name;
  return name ? `${amountText} — ${name}` : amountText;
}

function shopName(row) {
  return (row?.shop && String(row.shop).trim()) || `Mağaza #${row?.id}`;
}

function DiscountGroupMapTable({
  lookupData,
  highlightPropertyId = '',
  onPickShop,
  compact = false,
}) {
  if (!lookupData?.group) return null;

  const highlight = highlightPropertyId.trim().toLowerCase();
  const members = lookupData.members || [];

  return (
    <div className="simpra-global-lookup">
      <p className="simpra-muted">
        <strong>{lookupData.group.name}</strong>
        {' · global '}
        <span className="simpra-mono" title={lookupData.group.globalDiscountId}>
          {shortId(lookupData.group.globalDiscountId)}
        </span>
        {' · beklenen '}
        <strong>{lookupData.group.expectedLabel}</strong>
        {' · '}
        {members.length}/{KNOWN_SHOPS.length} şube
      </p>
      <div className="simpra-table-wrap">
        <table className="simpra-table">
          <thead>
            <tr>
              <th>Şube</th>
              {!compact ? <th>Property ID</th> : null}
              <th>Property discount ID</th>
              {!compact ? (
                <>
                  <th>Simpra catalog</th>
                  <th>Tutar / tip</th>
                  <th>Durum</th>
                </>
              ) : (
                <th />
              )}
            </tr>
          </thead>
          <tbody>
            {KNOWN_SHOPS.map((shop) => {
              const row = members.find(
                (m) => String(m.propertyId || '').toLowerCase() === shop.propertyId.toLowerCase(),
              );
              const isHighlight = highlight && shop.propertyId.toLowerCase() === highlight;
              if (!row) {
                return (
                  <tr key={shop.key} className="simpra-verify-row--missing">
                    <td>{shop.label}</td>
                    {!compact ? <td className="simpra-mono">{shortId(shop.propertyId)}</td> : null}
                    <td className="simpra-muted" colSpan={compact ? 1 : 3}>Eşleme yok</td>
                    {compact ? (
                      <td />
                    ) : (
                      <td><span className="simpra-verify-badge simpra-verify-badge--bad">Eksik</span></td>
                    )}
                  </tr>
                );
              }
              const simpraText = row.simpraValue != null
                ? (row.simpraType === 'amount' ? `${row.simpraValue} TL` : `%${row.simpraValue}`)
                : '—';
              const ok = row.amountMatch === true && row.typeMatch !== false && !row.verifyError;
              const warn = row.amountMatch === false || row.typeMatch === false;
              return (
                <tr
                  key={shop.key}
                  className={[
                    isHighlight ? 'simpra-verify-row--highlight' : '',
                    ok ? 'simpra-verify-row--ok' : warn ? 'simpra-verify-row--warn' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <td>{row.shopLabel || shop.label}</td>
                  {!compact ? (
                    <td><span className="simpra-mono" title={row.propertyId}>{shortId(row.propertyId)}</span></td>
                  ) : null}
                  <td>
                    <span className="simpra-mono" title={row.propertyDiscountId}>
                      {compact ? shortId(row.propertyDiscountId) : row.propertyDiscountId}
                    </span>
                  </td>
                  {!compact ? (
                    <>
                      <td>{row.simpraLabel || row.discountLabel || '—'}</td>
                      <td>
                        {simpraText}
                        {row.simpraType ? ` · ${row.simpraType}` : ''}
                      </td>
                      <td>
                        {row.verifyError ? (
                          <div className="simpra-verify-status">
                            <span className="simpra-verify-badge simpra-verify-badge--bad">Hata</span>
                            <span className="simpra-muted simpra-verify-detail">{row.verifyError}</span>
                          </div>
                        ) : ok ? (
                          <div className="simpra-verify-status">
                            <span className="simpra-verify-badge simpra-verify-badge--ok">Uyumlu</span>
                            {row.verifySecretKey ? (
                              <span className="simpra-muted simpra-verify-detail">{row.verifySecretKey}</span>
                            ) : null}
                          </div>
                        ) : warn ? (
                          <span className="simpra-verify-badge simpra-verify-badge--bad">Uyumsuz</span>
                        ) : (
                          <span className="simpra-verify-badge">—</span>
                        )}
                      </td>
                    </>
                  ) : (
                    <td className="simpra-actions">
                      {onPickShop ? (
                        <button
                          type="button"
                          className={`simpra-btn simpra-btn--ghost${isHighlight ? ' simpra-btn--secondary' : ''}`}
                          onClick={() => onPickShop(shop.propertyId, shop.label)}
                        >
                          {isHighlight ? 'Seçili' : 'Test'}
                        </button>
                      ) : null}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SimpraAdminPage() {
  const [shops, setShops] = useState([]);
  const [drafts, setDrafts] = useState(() => new Map());
  const [shopsLoading, setShopsLoading] = useState(true);
  const [shopsError, setShopsError] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [savingId, setSavingId] = useState(null);

  const [discounts, setDiscounts] = useState([]);
  const [discLoading, setDiscLoading] = useState(false);
  const [discError, setDiscError] = useState('');
  const [discFilter, setDiscFilter] = useState('');

  const [checkId, setCheckId] = useState('');
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError] = useState('');
  const [checkData, setCheckData] = useState(null);
  const [applyPropertyId, setApplyPropertyId] = useState('');

  const [testDiscountId, setTestDiscountId] = useState('');
  const [copied, setCopied] = useState('');

  const [secretSlots, setSecretSlots] = useState([]);
  const [secretKey, setSecretKey] = useState(DEFAULT_SECRET_KEY);
  const [secretsLoading, setSecretsLoading] = useState(false);
  const [secretsError, setSecretsError] = useState('');

  const [discountGroups, setDiscountGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState('');
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [groupGlobalId, setGroupGlobalId] = useState('');
  const [groupAmount, setGroupAmount] = useState('15');
  const [groupType, setGroupType] = useState('percentage');
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupMsg, setGroupMsg] = useState('');
  const [resolveTestPropertyId, setResolveTestPropertyId] = useState('');
  const [resolveTestResult, setResolveTestResult] = useState('');
  const [resolveTestLoading, setResolveTestLoading] = useState(false);
  const [globalLookupLoading, setGlobalLookupLoading] = useState(false);
  const [globalLookupError, setGlobalLookupError] = useState('');
  const [globalLookupData, setGlobalLookupData] = useState(null);
  const [discTypeFilter] = useState('');

  const loadSecrets = useCallback(async () => {
    setSecretsLoading(true);
    setSecretsError('');
    try {
      const res = await callEdgeFunction('simpra-proxy', {
        method: 'POST',
        body: { method: 'GET', path: '/__secrets' },
      });
      const list = Array.isArray(res?.secrets) ? res.secrets : [];
      setSecretSlots(list);
      setSecretKey((prev) => {
        if (list.some((s) => s.key === prev)) return prev;
        const preferred = list.find((s) => s.key === DEFAULT_SECRET_KEY);
        return preferred?.key || list[0]?.key || DEFAULT_SECRET_KEY;
      });
      if (!list.length) {
        setSecretsError(
          'Hiç configured secret yok. Her şube için …_APP, …_CONNECT ve …_ACCESS ayarlayın.',
        );
      }
    } catch (e) {
      setSecretSlots([]);
      const msg = e?.message || 'Secret listesi alınamadı (proxy deploy edilmeli).';
      setSecretsError(
        msg.includes('Could not find route matching') && msg.includes('__secrets')
          ? `${msg} — simpra-proxy yeniden deploy edilmeli (eski sürüm /__secrets’ı Simpra’ya iletiyor).`
          : msg,
      );
    } finally {
      setSecretsLoading(false);
    }
  }, []);

  const loadShops = useCallback(async () => {
    setShopsLoading(true);
    setShopsError('');
    const { data, error } = await supabase
      .from('shops')
      .select('id, shop, simpra_property_id')
      .order('id', { ascending: true });
    setShopsLoading(false);
    if (error) {
      setShopsError(
        error.message.includes('simpra_property_id')
          ? 'simpra_property_id sütunu yok. sql/81_shops_simpra_property_ids.sql çalıştırın.'
          : error.message,
      );
      setShops([]);
      return;
    }
    const rows = Array.isArray(data) ? data : [];
    setShops(rows);
    setDrafts(new Map(rows.map((r) => [r.id, r.simpra_property_id || ''])));
  }, []);

  const loadDiscounts = useCallback(async () => {
    // Wait until /__secrets has resolved so we never call with a UI-default
    // secretKey that is not configured on the server (proxy returns 400).
    if (secretsLoading) return;
    if (!secretSlots.length) {
      setDiscounts([]);
      setDiscError(
        secretsError
          || 'Configured secret yok — önce secret satırları Ayarlı olmalı, sonra listeyi yenileyin.',
      );
      return;
    }
    const key = secretSlots.some((s) => s.key === secretKey)
      ? secretKey
      : (secretSlots[0]?.key || '');
    if (!key) {
      setDiscounts([]);
      setDiscError('Geçerli secret seçilemedi.');
      return;
    }
    if (key !== secretKey) setSecretKey(key);

    setDiscLoading(true);
    setDiscError('');
    try {
      const res = await callEdgeFunction('simpra-proxy', {
        method: 'POST',
        body: {
          method: 'GET',
          path: '/discounts',
          secretKey: key,
        },
      });
      const list = Array.isArray(res?.discounts) ? res.discounts : [];
      setDiscounts(list);
      if (!list.length && res?.error) {
        setDiscError(typeof res.error === 'string' ? res.error : 'İndirim listesi boş.');
      }
    } catch (e) {
      setDiscounts([]);
      setDiscError(e?.message || 'Simpra indirimleri alınamadı.');
    } finally {
      setDiscLoading(false);
    }
  }, [secretKey, secretSlots, secretsLoading, secretsError]);

  const selectedSecret = useMemo(
    () => secretSlots.find((s) => s.key === secretKey) || null,
    [secretSlots, secretKey],
  );

  useEffect(() => {
    loadDiscounts();
  }, [loadDiscounts]);

  const resetGroupForm = useCallback(() => {
    setEditingGroupId(null);
    setGroupName('');
    setGroupGlobalId('');
    setGroupAmount('15');
    setGroupType('percentage');
    setGroupMembers([]);
    setGroupMsg('');
    setResolveTestResult('');
  }, []);

  const loadDiscountGroups = useCallback(async () => {
    setGroupsLoading(true);
    setGroupsError('');
    try {
      const { data, error } = await supabase
        .from('simpra_discount_groups')
        .select(`
          id,
          name,
          global_discount_id,
          discount_amount,
          discount_type,
          simpra_discount_group_members (
            id,
            property_id,
            property_discount_id,
            shop_label,
            discount_label,
            secret_key
          )
        `)
        .order('name', { ascending: true });
      if (error) {
        if (error.message.includes('simpra_discount_groups')) {
          throw new Error('simpra_discount_groups yok. sql/84_simpra_discount_groups.sql çalıştırın.');
        }
        throw error;
      }
      setDiscountGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      setDiscountGroups([]);
      setGroupsError(e?.message || 'İndirim grupları yüklenemedi.');
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  const startEditGroup = useCallback((group) => {
    if (!group) {
      resetGroupForm();
      return;
    }
    setEditingGroupId(group.id);
    setGroupName(group.name || '');
    setGroupGlobalId(group.global_discount_id || '');
    setGroupAmount(group.discount_amount != null ? String(group.discount_amount) : '15');
    setGroupType(group.discount_type || 'percentage');
    setGroupMembers(
      (group.simpra_discount_group_members || []).map((m) => ({
        property_id: m.property_id,
        property_discount_id: m.property_discount_id,
        shop_label: m.shop_label || '',
        discount_label: m.discount_label || '',
        secret_key: m.secret_key || '',
      })),
    );
    setGroupMsg('');
    setResolveTestResult('');
  }, [resetGroupForm]);

  const addDiscountToGroup = useCallback((discount) => {
    const propertyId = selectedSecret?.propertyId || applyPropertyId.trim();
    if (!propertyId) {
      setGroupMsg('Önce secret veya property ID seçin.');
      return;
    }
    const shopLabel = selectedSecret?.label?.split(' · ')[0]
      || KNOWN_SHOPS.find((s) => s.propertyId === propertyId)?.label
      || '';
    setGroupMembers((prev) => {
      const next = prev.filter((m) => m.property_id !== propertyId);
      next.push({
        property_id: propertyId,
        property_discount_id: discount.id,
        shop_label: shopLabel,
        discount_label: discountLabel(discount),
        secret_key: secretKey,
      });
      return next;
    });
    if (discount.type) setGroupType(discount.type);
    const amt = Number(discount.value);
    if (Number.isFinite(amt)) setGroupAmount(String(amt));
    setGroupMsg(`${shopLabel || 'Şube'} için property indirimi eklendi.`);
  }, [selectedSecret, applyPropertyId, secretKey]);

  const removeGroupMember = useCallback((propertyId) => {
    setGroupMembers((prev) => prev.filter((m) => m.property_id !== propertyId));
  }, []);

  const saveDiscountGroup = useCallback(async () => {
    const name = groupName.trim();
    const globalId = groupGlobalId.trim();
    if (!name || !globalId) {
      setGroupMsg('Grup adı ve global discount ID zorunlu.');
      return;
    }
    if (!groupMembers.length) {
      setGroupMsg('En az bir şube / property indirimi ekleyin (aşağıdaki listeden).');
      return;
    }
    setGroupSaving(true);
    setGroupMsg('');
    setGroupsError('');
    try {
      const amount = Number(groupAmount);
      const payload = {
        name,
        global_discount_id: globalId,
        discount_amount: Number.isFinite(amount) ? amount : null,
        discount_type: groupType || 'percentage',
        updated_at: new Date().toISOString(),
      };

      let groupId = editingGroupId;
      if (groupId) {
        const { error } = await supabase
          .from('simpra_discount_groups')
          .update(payload)
          .eq('id', groupId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('simpra_discount_groups')
          .insert(payload)
          .select('id')
          .single();
        if (error) throw error;
        groupId = data.id;
        setEditingGroupId(groupId);
      }

      const { error: delErr } = await supabase
        .from('simpra_discount_group_members')
        .delete()
        .eq('group_id', groupId);
      if (delErr) throw delErr;

      const { error: insErr } = await supabase
        .from('simpra_discount_group_members')
        .insert(
          groupMembers.map((m) => ({
            group_id: groupId,
            property_id: m.property_id,
            property_discount_id: m.property_discount_id,
            shop_label: m.shop_label || null,
            discount_label: m.discount_label || null,
            secret_key: m.secret_key || null,
          })),
        );
      if (insErr) throw insErr;

      setGroupMsg('İndirim grubu kaydedildi.');
      await loadDiscountGroups();
    } catch (e) {
      setGroupMsg(e?.message || 'Kayıt başarısız.');
    } finally {
      setGroupSaving(false);
    }
  }, [
    groupName,
    groupGlobalId,
    groupAmount,
    groupType,
    groupMembers,
    editingGroupId,
    loadDiscountGroups,
  ]);

  const deleteDiscountGroup = useCallback(async () => {
    if (!editingGroupId) return;
    if (!window.confirm('Bu indirim grubu silinsin mi?')) return;
    setGroupSaving(true);
    setGroupMsg('');
    try {
      const { error } = await supabase
        .from('simpra_discount_groups')
        .delete()
        .eq('id', editingGroupId);
      if (error) throw error;
      resetGroupForm();
      await loadDiscountGroups();
      setGroupMsg('Grup silindi.');
    } catch (e) {
      setGroupMsg(e?.message || 'Silinemedi.');
    } finally {
      setGroupSaving(false);
    }
  }, [editingGroupId, loadDiscountGroups, resetGroupForm]);

  const testResolveGroup = useCallback(async (propertyIdOverride) => {
    const gid = groupGlobalId.trim();
    if (!gid) {
      setResolveTestResult('Önce global discount ID girin.');
      return;
    }
    const pid = String(
      propertyIdOverride
      ?? resolveTestPropertyId.trim()
      ?? applyPropertyId.trim()
      ?? selectedSecret?.propertyId
      ?? '',
    ).trim();

    setResolveTestLoading(true);
    setResolveTestResult('');
    setGlobalLookupError('');

    try {
      const lookupRes = await callEdgeFunction('simpra-proxy', {
        method: 'POST',
        body: {
          method: 'POST',
          path: '/__lookup-discount-group',
          body: { globalDiscountId: gid, verify: !pid },
        },
      });
      if (lookupRes?.error) {
        setGlobalLookupData(null);
        setGlobalLookupError(lookupRes.error);
        setResolveTestResult(lookupRes.error);
        return;
      }
      setGlobalLookupData(lookupRes);

      if (!pid) {
        setResolveTestResult(
          `Grup yüklendi: ${lookupRes.group?.name || '—'} · ${lookupRes.members?.length || 0} şube. Tek şube testi için şube seçin.`,
        );
        return;
      }

      setResolveTestPropertyId(pid);
      const res = await callEdgeFunction('simpra-proxy', {
        method: 'POST',
        body: {
          method: 'POST',
          path: '/__resolve-discount',
          body: { preferredId: gid, propertyId: pid },
        },
      });
      if (res?.id) {
        const shopLabel = KNOWN_SHOPS.find((s) => s.propertyId.toLowerCase() === pid.toLowerCase())?.label;
        setResolveTestResult(
          `✓ ${res.resolvedVia || 'ok'}${shopLabel ? ` · ${shopLabel}` : ''} → property discount id ${res.id}${res.groupName ? ` (${res.groupName})` : ''}`,
        );
      } else {
        setResolveTestResult(res?.error || 'Çözülemedi.');
      }
    } catch (e) {
      const msg = e?.message || 'Resolve hatası.';
      setResolveTestResult(
        msg.includes('Could not find route matching') && (msg.includes('__lookup-discount-group') || msg.includes('__resolve-discount'))
          ? `${msg} — simpra-proxy yeniden deploy edilmeli.`
          : msg,
      );
    } finally {
      setResolveTestLoading(false);
    }
  }, [groupGlobalId, resolveTestPropertyId, applyPropertyId, selectedSecret]);

  const lookupGlobalDiscount = useCallback(async (globalIdOverride) => {
    const gid = String(globalIdOverride ?? groupGlobalId).trim();
    if (!gid) {
      setGlobalLookupError('Global discount ID girin.');
      setGlobalLookupData(null);
      return;
    }
    setGlobalLookupLoading(true);
    setGlobalLookupError('');
    setGlobalLookupData(null);
    try {
      const res = await callEdgeFunction('simpra-proxy', {
        method: 'POST',
        body: {
          method: 'POST',
          path: '/__lookup-discount-group',
          body: { globalDiscountId: gid, verify: true },
        },
      });
      if (res?.error) {
        setGlobalLookupError(res.error);
        const local = discountGroups.find(
          (g) => String(g.global_discount_id || '').toLowerCase() === gid.toLowerCase(),
        );
        if (local) {
          startEditGroup(local);
        }
        return;
      }
      setGlobalLookupData(res);
      const local = discountGroups.find(
        (g) => String(g.global_discount_id || '').toLowerCase() === gid.toLowerCase(),
      );
      if (local) {
        startEditGroup(local);
      } else if (res?.group) {
        setGroupName(res.group.name || '');
        setGroupGlobalId(res.group.globalDiscountId || gid);
        setGroupAmount(
          res.group.discountAmount != null ? String(res.group.discountAmount) : groupAmount,
        );
        setGroupType(res.group.discountType || 'percentage');
        if (Array.isArray(res.members)) {
          setGroupMembers(res.members.map((m) => ({
            property_id: m.propertyId,
            property_discount_id: m.propertyDiscountId,
            shop_label: m.shopLabel || '',
            discount_label: m.discountLabel || m.simpraLabel || '',
            secret_key: m.secretKey || '',
          })));
        }
      }
    } catch (e) {
      const msg = e?.message || 'Global ID kontrolü başarısız.';
      setGlobalLookupError(
        msg.includes('Could not find route matching') && msg.includes('__lookup-discount-group')
          ? `${msg} — simpra-proxy yeniden deploy edilmeli (eski sürüm bu yolu Simpra'ya iletiyor).`
          : msg,
      );
    } finally {
      setGlobalLookupLoading(false);
    }
  }, [groupGlobalId, discountGroups, startEditGroup, groupAmount]);

  useEffect(() => {
    loadShops();
    loadSecrets();
    loadDiscountGroups();
  }, [loadShops, loadSecrets, loadDiscountGroups]);

  const filteredDiscounts = useMemo(() => {
    const q = discFilter.trim().toLocaleLowerCase('tr-TR');
    let list = [...discounts].sort((a, b) => {
      const av = Number(a.value) || 0;
      const bv = Number(b.value) || 0;
      if (av !== bv) return av - bv;
      return discountLabel(a).localeCompare(discountLabel(b), 'tr-TR');
    });
    if (discTypeFilter) {
      list = list.filter((d) => d.type === discTypeFilter);
    }
    if (!q) return list;
    return list.filter((d) => {
      const hay = `${d.id} ${discountLabel(d)} ${d.type}`.toLocaleLowerCase('tr-TR');
      return hay.includes(q);
    });
  }, [discounts, discFilter, discTypeFilter]);

  const dirtyCount = useMemo(() => {
    let n = 0;
    shops.forEach((row) => {
      const draft = (drafts.get(row.id) || '').trim();
      const saved = (row.simpra_property_id || '').trim();
      if (draft !== saved) n += 1;
    });
    return n;
  }, [shops, drafts]);

  const setDraft = (shopId, value) => {
    setDrafts((prev) => {
      const next = new Map(prev);
      next.set(shopId, value);
      return next;
    });
    setSaveMsg('');
  };

  const applyKnown = (shopId, propertyId) => {
    setDraft(shopId, propertyId);
  };

  const saveShopProperty = async (shopId) => {
    const raw = (drafts.get(shopId) || '').trim();
    setSavingId(shopId);
    setSaveMsg('');
    setShopsError('');
    const payload = { simpra_property_id: raw || null };
    const { data, error } = await supabase
      .from('shops')
      .update(payload)
      .eq('id', shopId)
      .select('id, shop, simpra_property_id')
      .maybeSingle();
    setSavingId(null);
    if (error) {
      setShopsError(error.message);
      return;
    }
    if (!data) {
      setShopsError('Kayıt güncellenemedi (RLS?).');
      return;
    }
    setShops((prev) => prev.map((r) => (r.id === shopId ? { ...r, ...data } : r)));
    setDrafts((prev) => {
      const next = new Map(prev);
      next.set(shopId, data.simpra_property_id || '');
      return next;
    });
    setSaveMsg(`${shopName(data)} property ID kaydedildi.`);
  };

  const lookupCheck = async () => {
    const id = checkId.trim();
    if (!id) {
      setCheckError('Check ID girin.');
      return;
    }
    setCheckLoading(true);
    setCheckError('');
    setCheckData(null);
    try {
      const res = await callEdgeFunction('simpra-proxy', {
        method: 'POST',
        body: {
          method: 'GET',
          path: `/checks/${id}`,
          ...(secretKey ? { secretKey } : {}),
        },
      });
      const check = res?.check ?? res;
      if (!check?.id) {
        throw new Error(typeof res?.error === 'string' ? res.error : 'Check okunamadı.');
      }
      setCheckData(check);
      if (check.property_id) {
        setApplyPropertyId(String(check.property_id));
      }
    } catch (e) {
      setCheckError(e?.message || 'Check yüklenemedi.');
    } finally {
      setCheckLoading(false);
    }
  };

  const applyPropertyLabel = useMemo(() => {
    const pid = applyPropertyId.trim().toLowerCase();
    if (!pid) return null;
    const known = KNOWN_SHOPS.find((p) => p.propertyId.toLowerCase() === pid);
    if (known) return known.label;
    const shop = shops.find(
      (s) => String(s.simpra_property_id || '').toLowerCase() === pid,
    );
    return shop ? shopName(shop) : null;
  }, [applyPropertyId, shops]);

  const secretsSetupCommands = useMemo(() => {
    const lines = [
      '# 3 tokens per shop: MMJ APP + MMJ APP Connect + Simpra Access Token',
      '',
    ];
    KNOWN_SHOPS.forEach((shop) => {
      const shopUpper = shop.key.toUpperCase();
      lines.push(`# ${shop.label}`);
      lines.push(`supabase secrets set SIMPRA_TOKEN_${shopUpper}_APP="PASTE_MMJ_APP_TOKEN" \\`);
      lines.push(`  SIMPRA_TOKEN_${shopUpper}_CONNECT="PASTE_CONNECT_TOKEN" \\`);
      lines.push(`  SIMPRA_TOKEN_${shopUpper}_ACCESS="PASTE_SIMPRA_ACCESS_TOKEN" \\`);
      lines.push(`  ${shop.propertySecret}="${shop.propertyId}"`);
      lines.push('');
    });
    lines.push('supabase functions deploy simpra-proxy');
    lines.push('');
    lines.push('# Optional: unset pre-split shop names / numbered secrets');
    lines.push('# supabase secrets unset SIMPRA_TOKEN_BAHCESEHIR SIMPRA_ACCESS_TOKEN2 …');
    return lines.join('\n');
  }, []);

  const curlApply = useMemo(() => {
    const cid = checkId.trim() || 'CHECK_UUID';
    const did = testDiscountId.trim() || 'DISCOUNT_UUID';
    const pid = applyPropertyId.trim() || selectedSecret?.propertyId || 'PROPERTY_UUID';
    const label = applyPropertyLabel
      || selectedSecret?.label
      || '';
    const labelPart = label ? ` (${label})` : '';
    const tokenHint = secretKey || 'YOUR_PROPERTY_TOKEN';
    return [
      `# property_id: ${pid}${labelPart}`,
      `# secret slot: ${tokenHint}`,
      `# Use that property's access token (Scope: Property).`,
      `curl -sS -X POST \\`,
      `  "https://api.simpra.app/apps/api/v1/checks/${cid}/check_discounts" \\`,
      `  -H "Accept: application/json" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -H "Scope: Property" \\`,
      `  -H "Simpra-Access-Token: YOUR_PROPERTY_TOKEN" \\`,
      `  -d '{`,
      `    "discount_id": "${did}"`,
      `  }'`,
    ].join('\n');
  }, [checkId, testDiscountId, applyPropertyId, applyPropertyLabel, secretKey, selectedSecret]);

  const copyText = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied(''), 1500);
    } catch {
      setCopied('');
    }
  };

  return (
    <div className="admin-page-content simpra-page">
      <AdminPageHero
        kicker="Entegrasyon"
        title="Simpra"
        subtitle="Şube property ID eşlemesi ve mevcut access token ile indirim listesi. Org-level ID property token’da görünmezse apply ‘discount id cannot be found’ verir."
      >
        <button
          type="button"
          className="simpra-btn simpra-btn--secondary"
          onClick={() => {
            loadShops();
            loadSecrets();
            loadDiscounts();
          }}
          disabled={shopsLoading || discLoading || secretsLoading}
        >
          Yenile
        </button>
      </AdminPageHero>

      {shopsError ? (
        <p className="simpra-alert simpra-alert--error" role="alert">{shopsError}</p>
      ) : null}
      {saveMsg ? <p className="simpra-alert simpra-alert--ok">{saveMsg}</p> : null}

      <section className="simpra-card">
        <div className="simpra-card-head">
          <h2 className="simpra-card-title">Access token (secret) seç</h2>
          <p className="simpra-card-sub">
            Her şubede 3 token:{' '}
            <strong>MMJ APP</strong>, <strong>MMJ APP Connect</strong>,{' '}
            <strong>Simpra Access Token</strong>. Secret formatı:{' '}
            <code>SIMPRA_TOKEN_&#123;SHOP&#125;_APP</code> / <code>…_CONNECT</code> / <code>…_ACCESS</code>.
          </p>
        </div>

        <div className="simpra-toolbar">
          <label className="simpra-field" style={{ flex: '1 1 16rem', margin: 0 }}>
            <span>Secret (şube · uygulama)</span>
            <select
              className="simpra-select"
              value={secretKey}
              onChange={(e) => {
                const key = e.target.value;
                setSecretKey(key);
                const known = KNOWN_PROPERTIES.find((p) => p.secretKey === key);
                const slot = secretSlots.find((s) => s.key === key);
                const pid = slot?.propertyId || known?.propertyId;
                if (pid) setApplyPropertyId(pid);
              }}
              disabled={secretsLoading}
            >
              {(secretSlots.length
                ? secretSlots
                : KNOWN_PROPERTIES.map((p) => ({
                  key: p.secretKey,
                  label: p.label,
                  propertyId: p.propertyId,
                  configured: false,
                }))
              ).map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                  {s.propertyId ? ` · ${shortId(s.propertyId)}` : ''}
                  {s.configured === false ? ' · (henüz set değil?)' : ''}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="simpra-btn simpra-btn--secondary"
            onClick={loadSecrets}
            disabled={secretsLoading}
          >
            {secretsLoading ? '…' : 'Secret’ları yenile'}
          </button>
          <button
            type="button"
            className="simpra-btn simpra-btn--ghost"
            onClick={() => copyText('setup', secretsSetupCommands)}
          >
            {copied === 'setup' ? 'Komutlar kopyalandı' : 'Setup komutlarını kopyala'}
          </button>
        </div>

        {secretsError ? (
          <p className="simpra-alert simpra-alert--error" role="alert">{secretsError}</p>
        ) : null}

        <div className="simpra-table-wrap">
          <table className="simpra-table">
            <thead>
              <tr>
                <th>Şube</th>
                <th>Uygulama</th>
                <th>Secret</th>
                <th>Property ID secret</th>
                <th>Property UUID</th>
                <th>Durum</th>
              </tr>
            </thead>
            <tbody>
              {KNOWN_PROPERTIES.map((p) => {
                const slot = secretSlots.find((s) => s.key === p.secretKey);
                const ok = Boolean(slot?.configured);
                return (
                  <tr key={p.key}>
                    <td className="simpra-shop-name">{p.shopLabel}</td>
                    <td>{p.appLabel}</td>
                    <td><code className="simpra-mono">{p.secretKey}</code></td>
                    <td><code className="simpra-mono">{p.propertySecret}</code></td>
                    <td>
                      <span className="simpra-mono" title={p.propertyId}>{shortId(p.propertyId)}</span>
                    </td>
                    <td>
                      {secretsLoading
                        ? '…'
                        : ok
                          ? <span style={{ color: 'var(--ux-good)' }}>Ayarlı</span>
                          : <span style={{ color: 'var(--ux-bad)' }}>Eksik</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <pre className="simpra-code" style={{ marginTop: '0.75rem' }}>{secretsSetupCommands}</pre>
      </section>

      <section className="simpra-card">
        <div className="simpra-card-head">
          <h2 className="simpra-card-title">Şube → Property ID</h2>
          <p className="simpra-card-sub">
            `shops.simpra_property_id` — metadata. Seed: <code>sql/81_shops_simpra_property_ids.sql</code>
            {dirtyCount ? ` · ${dirtyCount} kaydedilmemiş` : ''}
          </p>
        </div>

        <div className="simpra-known">
          {KNOWN_SHOPS.map((p) => (
            <button
              key={p.key}
              type="button"
              className="simpra-chip"
              title={p.propertyId}
              onClick={() => copyText(`known-${p.key}`, p.propertyId)}
            >
              {p.label}
              <span className="simpra-mono">{shortId(p.propertyId)}</span>
              {copied === `known-${p.key}` ? ' ✓' : ''}
            </button>
          ))}
        </div>

        {shopsLoading ? (
          <p className="simpra-muted">Mağazalar yükleniyor…</p>
        ) : shops.length === 0 ? (
          <p className="simpra-muted">Mağaza yok.</p>
        ) : (
          <div className="simpra-table-wrap">
            <table className="simpra-table">
              <thead>
                <tr>
                  <th>Şube</th>
                  <th>Property ID</th>
                  <th>Hazır ID</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shops.map((row) => {
                  const draft = drafts.get(row.id) ?? '';
                  const dirty = draft.trim() !== (row.simpra_property_id || '').trim();
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="simpra-shop-name">{shopName(row)}</div>
                        <div className="simpra-muted">#{row.id}</div>
                      </td>
                      <td>
                        <input
                          className="simpra-input simpra-mono"
                          value={draft}
                          onChange={(e) => setDraft(row.id, e.target.value)}
                          placeholder="uuid…"
                          spellCheck={false}
                        />
                      </td>
                      <td>
                        <select
                          className="simpra-select"
                          value=""
                          onChange={(e) => {
                            if (e.target.value) applyKnown(row.id, e.target.value);
                          }}
                        >
                          <option value="">Seç…</option>
                          {KNOWN_SHOPS.map((p) => (
                            <option key={p.key} value={p.propertyId}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="simpra-btn simpra-btn--primary"
                          disabled={!dirty || savingId === row.id}
                          onClick={() => saveShopProperty(row.id)}
                        >
                          {savingId === row.id ? '…' : 'Kaydet'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="simpra-card">
        <div className="simpra-card-head">
          <h2 className="simpra-card-title">İndirim grupları (global → şube)</h2>
          <p className="simpra-card-sub">
            Kullanıcıda tek <strong>global discount ID</strong> (ör. <code>7d4ab82d-…</code>) tutulur.
            Apply sırasında check’in <code>property_id</code> ile bu gruptan şubeye özel Simpra ID seçilir.
          </p>
        </div>

        <div className="simpra-toolbar">
          <label className="simpra-field" style={{ flex: '1 1 12rem', margin: 0 }}>
            <span>Mevcut grup</span>
            <select
              className="simpra-select"
              value={editingGroupId || ''}
              onChange={(e) => {
                const id = e.target.value;
                if (!id) {
                  resetGroupForm();
                  return;
                }
                const g = discountGroups.find((x) => x.id === id);
                startEditGroup(g);
              }}
              disabled={groupsLoading}
            >
              <option value="">— Yeni grup —</option>
              {discountGroups.map((g) => {
                const amt = g.discount_amount != null ? Number(g.discount_amount) : null;
                const typeLabel = g.discount_type === 'amount'
                  ? (amt != null ? `${amt} TL` : 'Tutar')
                  : (amt != null ? `%${amt}` : '%');
                return (
                  <option key={g.id} value={g.id}>
                    {g.name} · {typeLabel} · {shortId(g.global_discount_id)}
                  </option>
                );
              })}
            </select>
          </label>
          <button
            type="button"
            className="simpra-btn simpra-btn--secondary"
            onClick={() => startEditGroup(null)}
            disabled={groupSaving}
          >
            Yeni grup
          </button>
          <button
            type="button"
            className="simpra-btn simpra-btn--secondary"
            onClick={loadDiscountGroups}
            disabled={groupsLoading}
          >
            {groupsLoading ? '…' : 'Grupları yenile'}
          </button>
        </div>

        {groupsError ? (
          <p className="simpra-alert simpra-alert--error" role="alert">{groupsError}</p>
        ) : null}
        {groupMsg ? (
          <p className={`simpra-alert ${groupMsg.includes('kaydedildi') || groupMsg.includes('silindi') || groupMsg.includes('eklendi') ? 'simpra-alert--ok' : ''}`}>
            {groupMsg}
          </p>
        ) : null}

        <div className="simpra-form-grid simpra-form-grid--apply">
          <label className="simpra-field">
            <span>Grup adı</span>
            <input
              className="simpra-input"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="%15 Misafir"
            />
          </label>
          <label className="simpra-field">
            <span>Global discount ID</span>
            <div className="simpra-input-row">
              <input
                className="simpra-input simpra-mono"
                value={groupGlobalId}
                onChange={(e) => {
                  setGroupGlobalId(e.target.value);
                  setGlobalLookupData(null);
                  setGlobalLookupError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    lookupGlobalDiscount();
                  }
                }}
                placeholder="7d4ab82d-67b7-4f79-9173-0ab0e48a9d3a"
                spellCheck={false}
              />
              <button
                type="button"
                className="simpra-btn simpra-btn--secondary"
                disabled={globalLookupLoading || !groupGlobalId.trim()}
                onClick={() => lookupGlobalDiscount()}
              >
                {globalLookupLoading ? '…' : 'Kontrol et'}
              </button>
              <button
                type="button"
                className="simpra-btn simpra-btn--ghost"
                disabled={!groupGlobalId.trim()}
                onClick={() => copyText('global-id', groupGlobalId.trim())}
              >
                {copied === 'global-id' ? '✓' : 'Kopyala'}
              </button>
            </div>
          </label>
          <label className="simpra-field">
            <span>İndirim türü</span>
            <div className="simpra-segment" role="group" aria-label="İndirim türü">
              <button
                type="button"
                className={`simpra-segment-btn${groupType === 'percentage' ? ' simpra-segment-btn--active' : ''}`}
                onClick={() => setGroupType('percentage')}
                aria-pressed={groupType === 'percentage'}
              >
                %
              </button>
              <button
                type="button"
                className={`simpra-segment-btn${groupType === 'amount' ? ' simpra-segment-btn--active' : ''}`}
                onClick={() => setGroupType('amount')}
                aria-pressed={groupType === 'amount'}
              >
                Tutar
              </button>
            </div>
          </label>
          <label className="simpra-field">
            <span>{groupType === 'amount' ? 'Tutar (TL)' : 'Yüzde (%)'}</span>
            <input
              className="simpra-input"
              value={groupAmount}
              onChange={(e) => setGroupAmount(e.target.value)}
              inputMode="decimal"
              placeholder={groupType === 'amount' ? '50' : '15'}
            />
          </label>
        </div>

        {globalLookupError ? (
          <p className="simpra-alert simpra-alert--error" role="alert">{globalLookupError}</p>
        ) : null}

        {globalLookupData?.group ? (
          <DiscountGroupMapTable lookupData={globalLookupData} />
        ) : null}

        <div className="simpra-table-wrap" style={{ marginTop: '0.75rem' }}>
          <table className="simpra-table">
            <thead>
              <tr>
                <th>Şube</th>
                <th>Property ID</th>
                <th>Property discount ID</th>
                <th>Simpra etiket</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {groupMembers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="simpra-muted">
                    Aşağıdaki indirim listesinden secret seçip <strong>Gruba ekle</strong> ile şube satırı ekleyin.
                  </td>
                </tr>
              ) : (
                groupMembers.map((m) => (
                  <tr key={m.property_id}>
                    <td>{m.shop_label || '—'}</td>
                    <td><span className="simpra-mono" title={m.property_id}>{shortId(m.property_id)}</span></td>
                    <td><span className="simpra-mono" title={m.property_discount_id}>{shortId(m.property_discount_id)}</span></td>
                    <td>{m.discount_label || '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="simpra-btn simpra-btn--ghost"
                        onClick={() => removeGroupMember(m.property_id)}
                      >
                        Kaldır
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="simpra-toolbar" style={{ marginTop: '0.75rem' }}>
          <button
            type="button"
            className="simpra-btn simpra-btn--primary"
            onClick={saveDiscountGroup}
            disabled={groupSaving}
          >
            {groupSaving ? '…' : 'Grubu kaydet'}
          </button>
          {editingGroupId ? (
            <button
              type="button"
              className="simpra-btn simpra-btn--ghost"
              onClick={deleteDiscountGroup}
              disabled={groupSaving}
            >
              Sil
            </button>
          ) : null}
        </div>

        <div className="simpra-resolve-test" style={{ marginTop: '1rem' }}>
          <h3 className="simpra-side-title">Resolve test (global → şube)</h3>
          <p className="simpra-muted" style={{ marginBottom: '0.65rem' }}>
            Global ID girince grup eşlemesi yüklenir. İsteğe bağlı şube seçerek tek property için çözümleme dener.
          </p>
          <div className="simpra-form-grid simpra-form-grid--apply">
            <label className="simpra-field">
              <span>Şube (property)</span>
              <select
                className="simpra-select"
                value={resolveTestPropertyId}
                onChange={(e) => setResolveTestPropertyId(e.target.value)}
              >
                <option value="">— Tüm grup —</option>
                {KNOWN_SHOPS.map((shop) => (
                  <option key={shop.key} value={shop.propertyId}>
                    {shop.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="simpra-field">
              <span>veya property ID</span>
              <input
                className="simpra-input simpra-mono"
                value={resolveTestPropertyId}
                onChange={(e) => setResolveTestPropertyId(e.target.value)}
                placeholder={selectedSecret?.propertyId || 'check property_id'}
                spellCheck={false}
              />
            </label>
            <div className="simpra-field" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="simpra-btn simpra-btn--secondary"
                onClick={() => testResolveGroup()}
                disabled={resolveTestLoading || globalLookupLoading || !groupGlobalId.trim()}
              >
                {resolveTestLoading ? '…' : 'Grubu göster / çözümle'}
              </button>
            </div>
          </div>
          {resolveTestResult ? (
            <p className="simpra-muted" style={{ marginTop: '0.35rem' }}>{resolveTestResult}</p>
          ) : null}
          {globalLookupData?.group ? (
            <DiscountGroupMapTable
              lookupData={globalLookupData}
              highlightPropertyId={resolveTestPropertyId}
              compact
              onPickShop={(propertyId, label) => {
                setResolveTestPropertyId(propertyId);
                testResolveGroup(propertyId);
              }}
            />
          ) : null}
        </div>
      </section>

      <section className="simpra-card">
        <div className="simpra-card-head">
          <h2 className="simpra-card-title">
            İndirimler
            {selectedSecret ? ` · ${selectedSecret.label}` : secretKey ? ` · ${secretKey}` : ''}
          </h2>
          <p className="simpra-card-sub">
            Seçili secret ile <code>GET /discounts</code>. Satırdan <strong>Gruba ekle</strong> ile şube eşlemesi yapın.
            Kullanıcıya global ID atanır; apply’de property ID ile çözülür.
          </p>
        </div>

        <div className="simpra-toolbar">
          <input
            className="simpra-input"
            value={discFilter}
            onChange={(e) => setDiscFilter(e.target.value)}
            placeholder="Ara: ad, %, uuid…"
          />
          <button
            type="button"
            className="simpra-btn simpra-btn--secondary"
            onClick={loadDiscounts}
            disabled={discLoading}
          >
            {discLoading ? 'Yükleniyor…' : 'Listeyi yenile'}
          </button>
        </div>

        {discError ? (
          <p className="simpra-alert simpra-alert--error" role="alert">{discError}</p>
        ) : null}

        {discLoading && !discounts.length ? (
          <p className="simpra-muted">İndirimler çekiliyor…</p>
        ) : (
          <div className="simpra-table-wrap">
            <table className="simpra-table">
              <thead>
                <tr>
                  <th>İndirim</th>
                  <th>Tip</th>
                  <th>ID</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredDiscounts.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="simpra-muted">
                      Kayıt yok.
                    </td>
                  </tr>
                ) : (
                  filteredDiscounts.map((d) => (
                    <tr key={d.id}>
                      <td>{discountLabel(d)}</td>
                      <td>{d.type || '—'}</td>
                      <td>
                        <span className="simpra-mono" title={d.id}>
                          {shortId(d.id)}
                        </span>
                      </td>
                      <td className="simpra-actions">
                        <button
                          type="button"
                          className="simpra-btn simpra-btn--ghost"
                          onClick={() => {
                            setTestDiscountId(d.id);
                            copyText(`disc-${d.id}`, d.id);
                          }}
                        >
                          {copied === `disc-${d.id}` ? 'Kopyalandı' : 'Kullan'}
                        </button>
                        <button
                          type="button"
                          className="simpra-btn simpra-btn--secondary"
                          onClick={() => addDiscountToGroup(d)}
                        >
                          Gruba ekle
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="simpra-card">
        <div className="simpra-card-head">
          <h2 className="simpra-card-title">Check & apply örneği</h2>
          <p className="simpra-card-sub">
            Check bilgisi proxy üzerinden (aynı server token). Apply curl’ü property token ile manuel test
            içindir.
          </p>
        </div>

        <div className="simpra-form-grid simpra-form-grid--apply">
          <label className="simpra-field">
            <span>Check ID</span>
            <input
              className="simpra-input simpra-mono"
              value={checkId}
              onChange={(e) => setCheckId(e.target.value)}
              placeholder="5ea25d19-7a82-4762-95cf-71f35fa39795"
              spellCheck={false}
            />
          </label>
          <label className="simpra-field">
            <span>Property ID</span>
            <input
              className="simpra-input simpra-mono"
              value={applyPropertyId}
              onChange={(e) => setApplyPropertyId(e.target.value)}
              placeholder="check’ten veya aşağıdan seç"
              spellCheck={false}
              list="simpra-property-id-options"
            />
            <datalist id="simpra-property-id-options">
              {KNOWN_SHOPS.map((p) => (
                <option key={p.key} value={p.propertyId}>
                  {p.label}
                </option>
              ))}
              {shops
                .filter((s) => s.simpra_property_id)
                .map((s) => (
                  <option key={s.id} value={s.simpra_property_id}>
                    {shopName(s)}
                  </option>
                ))}
            </datalist>
            {applyPropertyLabel ? (
              <span className="simpra-muted">{applyPropertyLabel}</span>
            ) : null}
          </label>
          <label className="simpra-field">
            <span>Hazır property</span>
            <select
              className="simpra-select"
              value=""
              onChange={(e) => {
                if (e.target.value) setApplyPropertyId(e.target.value);
              }}
            >
              <option value="">Seç…</option>
              {KNOWN_SHOPS.map((p) => (
                <option key={p.key} value={p.propertyId}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="simpra-field">
            <span>Discount ID (test)</span>
            <input
              className="simpra-input simpra-mono"
              value={testDiscountId}
              onChange={(e) => setTestDiscountId(e.target.value)}
              placeholder="listeden seç veya yapıştır"
              spellCheck={false}
            />
          </label>
        </div>

        <div className="simpra-toolbar">
          <button
            type="button"
            className="simpra-btn simpra-btn--primary"
            onClick={lookupCheck}
            disabled={checkLoading}
          >
            {checkLoading ? 'Yükleniyor…' : 'Check getir'}
          </button>
          <button
            type="button"
            className="simpra-btn simpra-btn--secondary"
            onClick={() => copyText('curl', curlApply)}
          >
            {copied === 'curl' ? 'Curl kopyalandı' : 'Apply curl kopyala'}
          </button>
        </div>

        {checkError ? (
          <p className="simpra-alert simpra-alert--error" role="alert">{checkError}</p>
        ) : null}

        {checkData ? (
          <div className="simpra-check-box">
            <div><strong>Check</strong> <span className="simpra-mono">{checkData.id}</span></div>
            <div>Status: {checkData.status || '—'}</div>
            <div>
              Property:{' '}
              <span className="simpra-mono">
                {checkData.property_id || '—'}
              </span>
              {checkData.property_id ? (
                <button
                  type="button"
                  className="simpra-btn simpra-btn--ghost"
                  style={{ marginLeft: '0.5rem' }}
                  onClick={() => setApplyPropertyId(String(checkData.property_id))}
                >
                  Forma al
                </button>
              ) : null}
            </div>
            <div>
              Discounts on check:{' '}
              {Array.isArray(checkData.check_discounts) && checkData.check_discounts.length
                ? checkData.check_discounts.length
                : 0}
            </div>
          </div>
        ) : null}

        <pre className="simpra-code">{curlApply}</pre>
      </section>
    </div>
  );
}
